import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

// Squadhatch "Fresh Hatch" design system — field green + egg-yolk amber,
// condensed athletic display type, pill buttons, soft elevated cards.
const CONDENSED = '"Barlow Condensed", "Arial Narrow", sans-serif';

const theme = createTheme({
  palette: {
    primary: { main: '#2E7D4F', dark: '#1B4332', light: '#E8F5E9' },
    secondary: { main: '#FFB300', contrastText: '#1B2B20' },
    background: { default: '#F5F7F4', paper: '#FFFFFF' },
    text: { primary: '#15241B', secondary: '#4E5D54' },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: CONDENSED, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.01em' },
    h2: { fontFamily: CONDENSED, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.01em' },
    h3: { fontFamily: CONDENSED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' },
    h4: { fontFamily: CONDENSED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 700, letterSpacing: '0.04em' },
  },
  components: {
    // Safety net: individual wide elements must scroll inside their own
    // containers (TableContainer etc.), never drag the whole page sideways
    MuiCssBaseline: {
      styleOverrides: {
        html: { overflowX: 'hidden' },
        body: { overflowX: 'hidden' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, paddingLeft: 20, paddingRight: 20 },
        sizeLarge: { paddingLeft: 32, paddingRight: 32, paddingTop: 12, paddingBottom: 12, fontSize: '1rem' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #E3EAE4',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(21, 36, 27, 0.10)' },
        },
      },
    },
    // Visible card borders make MUI's default 16px padding read cramped —
    // give all card content consistent breathing room
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: { padding: '4px 20px 16px' },
      },
    },
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: 16 } },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: { background: 'linear-gradient(105deg, #12291C 0%, #1B4332 55%, #2E7D4F 100%)' },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// Register service worker and handle update lifecycle
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Check for updates once per hour
        setInterval(() => registration.update(), 60 * 60 * 1000);

        // A new SW found while one is already active = update is available
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Notify App to show the update toast
              window.dispatchEvent(new CustomEvent('swUpdateAvailable', {
                detail: { registration },
              }));
            }
          });
        });
      })
      .catch((err) => console.warn('Service worker registration failed:', err));

    // When SKIP_WAITING fires and the SW activates, reload to get fresh assets
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) { refreshing = true; window.location.reload(); }
    });
  });
}
